// FactoryLineSettings — the Settings tab of a factory line: the line's own
// settings (target/mode/review gate/profile) plus INLINE editing of the
// referenced channel profile (which is shared across lines).

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import type { ChannelProfileInput } from '@/api/client'
import type { FactoryConfig, FactoryConfigInput, RenderMode } from '@/api/types'
import ChannelProfileForm from './ChannelProfileForm'
import { Button, Card, FormRow, Input, Select } from '@/admin-design/_common'

export default function FactoryLineSettings({ config }: { config: FactoryConfig }) {
  const qc = useQueryClient()
  const profilesQ = useQuery({ queryKey: ['channel-profiles'], queryFn: api.listProfiles })
  const profileQ = useQuery({
    queryKey: ['channel-profile', config.channel_profile_id],
    queryFn: () => api.getProfile(config.channel_profile_id),
  })

  const [name, setName] = useState(config.name)
  const [dailyTarget, setDailyTarget] = useState(config.daily_target)
  const [mode, setMode] = useState<RenderMode>(config.render_mode)
  const [reviewGate, setReviewGate] = useState(config.review_gate)
  const [storyFormat, setStoryFormat] = useState<'short' | 'narrative'>(config.story_format)
  const [targetLen, setTargetLen] = useState(config.target_length_sec)
  const [minScenes, setMinScenes] = useState(config.min_scenes)
  const [tags, setTags] = useState(config.tags ?? '')

  const toInput = (over?: Partial<FactoryConfigInput>): FactoryConfigInput => ({
    name: name.trim() || config.name,
    channel_profile_id: config.channel_profile_id,
    daily_target: dailyTarget > 0 ? dailyTarget : 1,
    render_mode: mode,
    default_visibility: config.default_visibility,
    enabled: config.enabled,
    review_gate: reviewGate,
    story_format: storyFormat,
    target_length_sec: targetLen >= 0 ? targetLen : 60,
    min_scenes: minScenes >= 0 ? minScenes : 0,
    tags: tags.trim(),
    // Preserve the line's source-selection mode (edited on the Sources page) so
    // saving these settings doesn't reset it.
    select_mode: config.select_mode,
    min_score: config.min_score,
    spike_only: config.spike_only,
    ...over,
  })

  const saveConfig = useMutation({
    mutationFn: (input: FactoryConfigInput) => api.updateFactoryConfig(config.id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['factory-configs'] }),
  })
  const saveProfile = useMutation({
    mutationFn: (input: ChannelProfileInput) => api.updateProfile(config.channel_profile_id, input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['channel-profile', config.channel_profile_id] }),
  })

  const profiles = profilesQ.data ?? []

  return (
    <div className="space-y-4">
      {/* Line settings */}
      <Card>
        <Card.Body className="space-y-3 p-4">
          <h3 className="text-sm font-semibold text-card-foreground">라인 설정</h3>
          <div className="grid min-w-0 gap-3 sm:grid-cols-2">
            <FormRow label="이름">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </FormRow>
            <FormRow label="하루 목표">
              <Input
                type="number"
                min={1}
                value={dailyTarget}
                onChange={(e) => setDailyTarget(Number(e.target.value))}
              />
            </FormRow>
            <FormRow label="렌더 모드">
              <Select
                value={mode}
                onValueChange={(v) => setMode(v as RenderMode)}
                options={[
                  { label: 'fast (프리뷰)', value: 'fast' },
                  { label: 'full (2K)', value: 'full' },
                ]}
              />
            </FormRow>
            <FormRow label="프로필">
              <Select
                value={String(config.channel_profile_id)}
                onValueChange={(v) => saveConfig.mutate(toInput({ channel_profile_id: Number(v) }))}
                options={profiles.map((p) => ({ label: p.name, value: String(p.id) }))}
              />
            </FormRow>
            <FormRow label="스토리 포맷" help="narrative=긴·다장면 / short=1~3컷">
              <div className="flex rounded-md border border-border p-0.5">
                {(['narrative', 'short'] as const).map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setStoryFormat(f)}
                    className={
                      'flex-1 rounded px-2 py-1 text-xs font-medium transition ' +
                      (storyFormat === f
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground')
                    }
                  >
                    {f}
                  </button>
                ))}
              </div>
            </FormRow>
            <FormRow label="목표 길이(초)">
              <Input
                type="number"
                min={0}
                value={targetLen}
                onChange={(e) => setTargetLen(Number(e.target.value))}
              />
            </FormRow>
            <FormRow label="최소 씬 수" help="0=자동">
              <Input
                type="number"
                min={0}
                value={minScenes}
                onChange={(e) => setMinScenes(Number(e.target.value))}
              />
            </FormRow>
            <FormRow
              label="트렌드 태그"
              help="이 라인이 받을 카테고리 (CSV, ?tag=). 예: community 또는 community,korean. 비우면 전역 설정 사용"
            >
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="community,korean"
              />
            </FormRow>
          </div>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={reviewGate}
              onChange={(e) => setReviewGate(e.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            검토 후 렌더 (반자동) — 스토리 생성 뒤 멈추고 스토리 탭에서 [진행]
          </label>
          {saveConfig.isError && (
            <p className="text-xs text-destructive">{errorMessage(saveConfig.error)}</p>
          )}
          <div className="flex justify-end border-t border-border pt-2">
            <Button
              type="button"
              variant="primary"
              size="sm"
              loading={saveConfig.isPending}
              onClick={() => saveConfig.mutate(toInput())}
            >
              라인 저장
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Inline profile edit */}
      <Card>
        <Card.Body className="p-4">
          <div className="mb-2 flex items-center gap-2">
            <h3 className="text-sm font-semibold text-card-foreground">프로필 (콘텐츠 페르소나)</h3>
            <span className="text-[11px] text-warning">
              ⚠ 이 프로필은 다른 라인과 공유될 수 있어요
            </span>
          </div>
          {profileQ.isLoading ? (
            <p className="text-xs text-muted-foreground">프로필 불러오는 중…</p>
          ) : profileQ.data ? (
            <ChannelProfileForm
              initial={profileQ.data}
              busy={saveProfile.isPending}
              error={saveProfile.isError ? errorMessage(saveProfile.error) : null}
              onCancel={() => profileQ.refetch()}
              onSubmit={(input) => saveProfile.mutate(input)}
            />
          ) : (
            <p className="text-xs text-destructive">프로필을 불러오지 못했어요.</p>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message
  if (e instanceof Error) return e.message
  return 'unknown error'
}
